/**
 * API Service - Handles all REST API calls to the backend
 */

import {
  GlobalMetrics,
  Alert,
  Post,
  Topic,
  DashboardStats,
  PaginatedResponse,
  FilterOptions,
} from '../types';

import { API_BASE_URL as BASE_URL } from '../config';
const NORMALIZED_BASE_URL = BASE_URL.replace(/\/$/, '');
const API_BASE_URL = NORMALIZED_BASE_URL.endsWith('/api')
  ? NORMALIZED_BASE_URL
  : `${NORMALIZED_BASE_URL}/api`;

export type MonitoringScopeType = 'GLOBAL' | 'NATIONAL' | 'REGIONAL';

export interface MonitoringScopeParams {
  scope_type?: MonitoringScopeType;
  country_code?: string | null;
  region_code?: string | null;
}

export interface MonitoringPreferenceResponse {
  user_id: number;
  location_input: string | null;
  location_country: string | null;
  location_region: string | null;
  profile_completed: boolean;
  scope_type: MonitoringScopeType;
  default_scope: boolean;
  country_code: string | null;
  region_code: string | null;
  region_label: string | null;
  updated_at: string | null;
}

export interface MonitoringPreferenceUpdateRequest {
  scope_type?: MonitoringScopeType;
  country_code?: string | null;
  region_code?: string | null;
  region_label?: string | null;
  location_input?: string | null;
  target_user_id?: number;
}

export interface IntelligenceFinding {
  entity: string;
  type: 'IP' | 'Domain' | 'Email' | 'Phone' | 'Business' | 'Sanctioned' | 'ArchivedURL' | 'EXIFData' | 'DeepfakeScan' | 'EvidenceCapture' | 'GeoLocation' | 'FlightTrack' | 'VesselTrack' | 'CrisisZone' | 'AIGeneratedImage' | 'LLMText' | 'VoiceClone' | 'NeuralWatermark';
  suite: 'cyber' | 'identity' | 'compliance' | 'media' | 'geo' | 'neural';
  reputation: string;
  risk_score: number;
  source: string;
  timestamp: string;
  associated_post: string;
  country?: string;
  abuse_reports?: number;
  isp?: string;
  asn?: string;
  threat_categories?: string[];
  email_breaches?: string[];
  phone_carrier?: string;
  line_type?: string;
  username_scan_results?: string[];
  related_posts?: string[];
  recommendations?: string[];
  impact_context?: string;
  correlation_tags?: string[];
  narrative_footprint?: { narrative: string; exposure: number; platforms: string[] }[];
  velocity_projection?: { impressions_48h: number; viral_probability: number; peak_hour: string };
  risk_breakdown?: { label: string; pct: number; color: string }[];
  company_number?: string;
  jurisdiction?: string;
  sanctions_list?: string;
  archive_url?: string;
  archive_timestamp?: string;
  sha256_hash?: string;
  media_url?: string;
  deepfake_score?: number;
  exif_metadata?: Record<string, string>;
  lat?: number;
  lon?: number;
  resolved_location?: string;
  crisis_zone?: string;
  crisis_severity?: 'critical' | 'high' | 'medium';
  distance_km?: number;
  callsign?: string;
  flight_origin?: string;
  altitude_m?: number;
  velocity_ms?: number;
  on_ground?: boolean;
  vessel_name?: string;
  vessel_type?: string;
  speed_kn?: number;
  destination?: string;
  mmsi?: string;
  ai_confidence?: number;
  generation_method?: string;
  model_attribution?: string;
  perplexity_score?: number;
  burstiness_score?: number;
  watermark_status?: 'present' | 'absent' | 'tampered';
  c2pa_issuer?: string;
  frequency_anomaly?: boolean;
  formant_deviation?: number;
}

function appendScopeParams(params: URLSearchParams, scope?: MonitoringScopeParams) {
  if (!scope) return;
  if (scope.scope_type) params.append('scope_type', scope.scope_type);
  if (scope.country_code) params.append('country_code', scope.country_code);
  if (scope.region_code) params.append('region_code', scope.region_code);
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  const mergedHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers: mergedHeaders,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const errorPayload = await response.json();
      detail = errorPayload?.detail || errorPayload?.message || detail;
    } catch {
      // Keep the HTTP status text when the error body is not JSON.
    }
    throw new Error(`API Error: ${response.status} ${detail}`);
  }

  return response.json() as Promise<T>;
}

/**
 * API Service Object
 */
export const apiService = {
  /**
   * Metrics Endpoints
   */
  metrics: {
    /**
     * Get global metrics (total posts, alerts, etc.)
     */
    getGlobalMetrics: async (scope?: MonitoringScopeParams): Promise<GlobalMetrics> => {
      const params = new URLSearchParams();
      appendScopeParams(params, scope);
      const q = params.toString();
      return fetchApi<GlobalMetrics>(`/metrics/global${q ? `?${q}` : ''}`);
    },

    /**
     * Get dashboard stats (metrics + trends + distributions)
     */
    getDashboardStats: async (scope?: MonitoringScopeParams): Promise<DashboardStats> => {
      const params = new URLSearchParams();
      appendScopeParams(params, scope);
      const q = params.toString();
      return fetchApi<DashboardStats>(`/metrics/dashboard-stats${q ? `?${q}` : ''}`);
    },

    /**
     * Get risk distribution over time
     */
    getRiskTrend: async (days: number = 7, scope?: MonitoringScopeParams): Promise<any[]> => {
      const params = new URLSearchParams({ days: days.toString() });
      appendScopeParams(params, scope);
      return fetchApi<any[]>(`/metrics/risk-trend?${params.toString()}`);
    },

    /**
     * Get sentiment trend
     */
    getSentimentTrend: async (days: number = 7, scope?: MonitoringScopeParams): Promise<any[]> => {
      const params = new URLSearchParams({ days: days.toString() });
      appendScopeParams(params, scope);
      return fetchApi<any[]>(`/metrics/sentiment-trend?${params.toString()}`);
    },

    /**
     * Get alert frequency trend
     */
    getAlertTrend: async (days: number = 7, scope?: MonitoringScopeParams): Promise<any[]> => {
      const params = new URLSearchParams({ days: days.toString() });
      appendScopeParams(params, scope);
      return fetchApi<any[]>(`/metrics/alert-trend?${params.toString()}`);
    },
  },

  /**
   * Alert Endpoints
   */
  alerts: {
    /**
     * Get all alerts with optional filtering
     */
    getAlerts: async (
      page: number = 1,
      pageSize: number = 10,
      filters?: FilterOptions,
      scope?: MonitoringScopeParams
    ): Promise<PaginatedResponse<Alert>> => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filters?.severity?.length) {
        params.append('severity', filters.severity.join(','));
      }
      if (filters?.riskLevel?.length) {
        params.append('riskLevel', filters.riskLevel.join(','));
      }
      if (filters?.dateRange) {
        params.append('startDate', filters.dateRange.start);
        params.append('endDate', filters.dateRange.end);
      }
      appendScopeParams(params, scope);

      return fetchApi<PaginatedResponse<Alert>>(`/alerts?${params}`);
    },

    /**
     * Get alert by ID
     */
    getAlertById: async (alertId: string): Promise<Alert> => {
      return fetchApi<Alert>(`/alerts/${alertId}`);
    },

    /**
     * Get recent critical alerts
     */
    getCriticalAlerts: async (limit: number = 5, scope?: MonitoringScopeParams): Promise<Alert[]> => {
      const params = new URLSearchParams({ limit: limit.toString() });
      appendScopeParams(params, scope);
      return fetchApi<Alert[]>(`/alerts/critical?${params.toString()}`);
    },

    /**
     * Resolve an alert
     */
    resolveAlert: async (
      alertId: string,
      notes?: string
    ): Promise<Alert> => {
      return fetchApi<Alert>(`/alerts/${alertId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ notes, resolvedAt: new Date().toISOString() }),
      });
    },

    /**
     * Dismiss an alert
     */
    dismissAlert: async (alertId: string): Promise<Alert> => {
      return fetchApi<Alert>(`/alerts/${alertId}/dismiss`, {
        method: 'POST',
      });
    },
  },

  /**
   * Post Endpoints
   */
  posts: {
    /**
     * Get posts with optional filtering
     */
    getPosts: async (
      page: number = 1,
      pageSize: number = 20,
      filters?: FilterOptions,
      scope?: MonitoringScopeParams
    ): Promise<PaginatedResponse<Post>> => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filters?.riskLevel?.length) {
        params.append('riskLevel', filters.riskLevel.join(','));
      }
      if (filters?.platform?.length) {
        params.append('platform', filters.platform.join(','));
      }
      if (filters?.searchText) {
        params.append('search', filters.searchText);
      }
      appendScopeParams(params, scope);

      return fetchApi<PaginatedResponse<Post>>(`/posts?${params}`);
    },

    /**
     * Get post by ID
     */
    getPostById: async (postId: string): Promise<Post> => {
      return fetchApi<Post>(`/posts/${postId}`);
    },

    /**
     * Get trending posts
     */
    getTrendingPosts: async (limit: number = 10): Promise<Post[]> => {
      return fetchApi<Post[]>(`/posts/trending?limit=${limit}`);
    },

    /**
     * Analyze a post (runs it through the AI pipeline)
     */
    analyzePost: async (text: string, platform: string): Promise<Post> => {
      return fetchApi<Post>('/posts/analyze', {
        method: 'POST',
        body: JSON.stringify({ text, platform }),
      });
    },

    /**
     * Flag a post for manual review
     */
    flagPost: async (postId: string, reason: string): Promise<Post> => {
      return fetchApi<Post>(`/posts/${postId}/flag`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },

    /**
     * Get cross-channel mentions of the same story across platforms
     */
    getCrossChannel: async (postId: string | number): Promise<any> => {
      return fetchApi<any>(`/posts/${postId}/cross-channel`);
    },

    /**
     * Get spread analysis — velocity, platform progression, risk timeline
     */
    getSpreadAnalysis: async (postId: string | number): Promise<any> => {
      return fetchApi<any>(`/posts/${postId}/spread`);
    },
  },

  /**
   * Topic Endpoints
   */
  topics: {
    /**
     * Get trending topics
     */
    getTrendingTopics: async (limit: number = 10): Promise<Topic[]> => {
      return fetchApi<Topic[]>(`/topics/trending?limit=${limit}`);
    },

    /**
     * Get topic by ID
     */
    getTopicById: async (topicId: string): Promise<Topic> => {
      return fetchApi<Topic>(`/topics/${topicId}`);
    },

    /**
     * Get topic posts
     */
    getTopicPosts: async (
      topicId: string,
      page: number = 1,
      pageSize: number = 20
    ): Promise<PaginatedResponse<Post>> => {
      return fetchApi<PaginatedResponse<Post>>(
        `/topics/${topicId}/posts?page=${page}&pageSize=${pageSize}`
      );
    },
  },

  /**
   * Analytics Endpoints
   */
  analytics: {
    /**
     * Get comprehensive analytics
     */
    getAnalytics: async (
      startDate: string,
      endDate: string,
      scope?: MonitoringScopeParams
    ): Promise<any> => {
      const params = new URLSearchParams({ startDate, endDate });
      appendScopeParams(params, scope);
      return fetchApi<any>(`/analytics?${params.toString()}`);
    },

    /**
     * Get region-based statistics
     */
    getRegionStats: async (region: string): Promise<any> => {
      return fetchApi<any>(`/analytics/regions/${region}`);
    },

    /**
     * Get network graph data (Phase 10)
     */
    getNetworkGraph: async (limit: number = 100, scope?: MonitoringScopeParams): Promise<any> => {
      const params = new URLSearchParams({ limit: limit.toString() });
      appendScopeParams(params, scope);
      return fetchApi<any>(`/analytics/network-graph?${params.toString()}`);
    },

    /**
     * Get crisis clusters (Phase 10)
     */
    getCrisisClusters: async (scope?: MonitoringScopeParams): Promise<any[]> => {
      const params = new URLSearchParams();
      appendScopeParams(params, scope);
      const q = params.toString();
      return fetchApi<any[]>(`/analytics/crisis-clusters${q ? `?${q}` : ''}`);
    },

    /**
     * Get temporal/platform hotspots (Phase 10)
     */
    getHotspots: async (scope?: MonitoringScopeParams): Promise<any[]> => {
      const params = new URLSearchParams();
      appendScopeParams(params, scope);
      const q = params.toString();
      return fetchApi<any[]>(`/analytics/hotspots${q ? `?${q}` : ''}`);
    },

    getIntelligenceFindings: async (
      limit: number = 60,
      scope?: MonitoringScopeParams
    ): Promise<IntelligenceFinding[]> => {
      const params = new URLSearchParams({ limit: String(limit) });
      appendScopeParams(params, scope);
      return fetchApi<IntelligenceFinding[]>(`/intelligence/findings?${params.toString()}`);
    },
  },

  auth: {
    getMonitoringPreference: async (): Promise<MonitoringPreferenceResponse> => {
      const token = localStorage.getItem('access_token');
      return fetchApi<MonitoringPreferenceResponse>('/auth/preferences/monitoring', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    },

    updateMonitoringPreference: async (
      body: MonitoringPreferenceUpdateRequest
    ): Promise<MonitoringPreferenceResponse> => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('401 Session expired');
      }

      const doRequest = () => fetchApi<MonitoringPreferenceResponse>('/auth/preferences/monitoring', {
        method: 'PUT',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(body),
      });

      try {
        return await doRequest();
      } catch (err: any) {
        const msg = String(err?.message || '').toLowerCase();
        const networkFailure = msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('load failed');
        if (!networkFailure) {
          throw err;
        }
        return await doRequest();
      }
    },
  },

  /**
   * Health Check
   */
  health: {
    /**
     * Check API health status
     */
    check: async (): Promise<any> => {
      return fetchApi<any>('/health');
    },
  },
};

export default apiService;
